import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import BigPictureBoard from './BigPictureBoard';
import FleetBoard from './FleetBoard';
import MaterialHaul from './MaterialHaul';
import TrafficControl from './TrafficControl';
import AdminLayout from './AdminLayout';
import WeekSchedule from './WeekSchedule';
import { apiFetch } from '../../services/apiClient';

// Schedule hub: three tiles — the weekly crew Schedule (live from QuickBooks Time)
// and a multi-layer operations Map (Jobs, Equipment, Who's Working and the CDL
// trucks, live from Azuga).

const fmtTime = iso => { if (!iso) return ''; const dt = new Date(iso); return isNaN(dt) ? '' : dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }); };
const navBtn = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: '7px 12px', fontSize: 13, fontWeight: 600, color: 'var(--text)', cursor: 'pointer' };

const PANES = ['schedule', 'map', 'moves', 'bigpicture', 'fleet', 'haul', 'traffic'];
export default function AdminSchedule() {
  // The open pane lives in the address (?pane=bigpicture) so Back from a job brings you to the Big
  // Picture you left, not the hub (Ryan, Nov 1 2026); null is the hub. replace: a pane is not a page.
  const [sp, setSp] = useSearchParams();
  const pane = PANES.includes(sp.get('pane')) ? sp.get('pane') : null;
  const setPane = p => setSp(p ? { pane: p } : {}, { replace: true });
  return (
    <AdminLayout>
      {pane === null && <Hub onPick={setPane} />}
      {pane === 'schedule' && <WeekSchedule onBack={() => setPane(null)} />}
      {pane === 'map' && <MapView onBack={() => setPane(null)} />}
      {pane === 'moves' && <MoveLogView onBack={() => setPane(null)} />}
      {pane === 'bigpicture' && <BigPictureBoard onBack={() => setPane(null)} />}
      {pane === 'fleet' && <FleetBoard onBack={() => setPane(null)} />}
      {pane === 'haul' && <MaterialHaul onBack={() => setPane(null)} />}
      {pane === 'traffic' && <TrafficControl onBack={() => setPane(null)} />}
    </AdminLayout>
  );
}

// ── Hub ───────────────────────────────────────────────────────────────────────
function Hub({ onPick }) {
  const tile = (key, icon, title, sub, color) => (
    <button key={key} onClick={() => onPick(key)}
      style={{ textAlign: 'left', background: 'var(--surface)', border: '1px solid var(--border)', borderTop: `4px solid ${color}`, borderRadius: 14, padding: 22, cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 8 }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.09)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}>
      <div style={{ fontSize: 34 }}>{icon}</div>
      <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--text)' }}>{title}</div>
      <div style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.4 }}>{sub}</div>
    </button>
  );
  return (
    <div>
      <h1 style={{ fontSize: 22, fontWeight: 800, margin: '0 0 4px' }}>Schedule and General Information</h1>
      <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginBottom: 18 }}>Crew scheduling, the field operations map &amp; company-wide information.</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16, maxWidth: 720 }}>
        {tile('schedule', '🗓️', 'Schedule', 'Weekly crew schedule — live from QuickBooks Time.', '#1e3a5f')}
        {tile('map', '🗺️', 'Map', 'Jobs, equipment, trucks & who’s working — layers you can turn on and off.', '#065f46')}
        {tile('moves', '🚚', 'Move Log', 'Every equipment move the shop has logged — searchable.', '#7c2d12')}
        {tile('bigpicture', '📋', 'Big Picture', 'Dirt and electrical dates for every job — and who’s actually on them.', '#a16207')}
        {tile('fleet', '🚛', 'Fleet', 'The trucks, live from Azuga GPS — where they are, what they did today, odometer & service.', '#4c1d95')}
        {tile('haul', '⛰️', 'Material & Hauling', 'Sand, base or fill in — or spoils out: nearest pits and dumps by the road, material and trucking priced, then Request a Truck.', '#92400e')}
        {tile('traffic', '🚧', 'Traffic Control', 'Site-specific traffic control plans on the satellite — signs, cone taper and buffer per MUTCD, printed for permits.', '#c2410c')}
      </div>
    </div>
  );
}

// ── Map ─────────────────────────────────────────────────────────────────────
const RENO = [39.5296, -119.8138];
const LAYER_META = {
  jobs:      { label: 'Jobs',          color: '#1d4ed8', fill: '#3b82f6' },
  equipment: { label: 'Equipment',     color: '#c2560f', fill: '#f97316' },
  crew:      { label: 'Who’s Working', color: '#15803d', fill: '#22c55e' },
  trucks:    { label: '🚚 Trucks',     color: '#6d28d9', fill: '#8b5cf6' },
};

// What the truck is doing right now, in the words you'd use out loud.
const TRUCK_STATE = {
  moving:   { word: 'Moving',        dot: '#8b5cf6' },
  idling:   { word: 'Idling',        dot: '#f59e0b' },
  speeding: { word: 'Over the limit', dot: '#dc2626' },
  stopped:  { word: 'Stopped',       dot: '#9ca3af' },
  unknown:  { word: '',              dot: '#8b5cf6' },
};

// "3 minutes ago" reads better than a timestamp when the whole point is freshness.
function ago(iso) {
  if (!iso) return '';
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  return `${Math.round(hrs / 24)} days ago`;
}

// Fit the map to whatever points are currently visible.
function FitBounds({ points }) {
  const map = useMap();
  const key = points.map(p => `${p[0].toFixed(4)},${p[1].toFixed(4)}`).join('|');
  useEffect(() => {
    if (points.length === 1) map.setView(points[0], 13);
    else if (points.length > 1) map.fitBounds(points, { padding: [45, 45], maxZoom: 14 });
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

function MapView({ onBack }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [layers, setLayers] = useState({ jobs: true, equipment: true, crew: true, trucks: true });

  const load = () => apiFetch('/map/data').then(d => { setData(d); setErr(''); }).catch(e => setErr(e?.error || 'Could not load the map.'));
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, []);

  const jobs = data?.jobs || [];
  const equipment = data?.equipment || [];
  const crew = data?.crew || [];
  const trucks = data?.trucks || [];
  const canSeeCrew = !!data?.canSeeCrew;
  const trucksOn = !!data?.trucksOn;

  // Spread crew who share the same coordinate (e.g. all pinned to one job) so they
  // don't stack into a single dot.
  const crewPlaced = useMemo(() => {
    const seen = {};
    return crew.map(c => {
      const k = `${c.lat.toFixed(4)},${c.lng.toFixed(4)}`;
      const n = seen[k] = (seen[k] || 0);
      seen[k] = n + 1;
      const ring = Math.floor(n / 8), idx = n % 8;
      const off = 0.0005 * (ring + 1);
      return { ...c, plat: c.lat + Math.cos(idx * Math.PI / 4) * off, plng: c.lng + Math.sin(idx * Math.PI / 4) * off };
    });
  }, [crew]);

  const points = [
    ...(layers.jobs ? jobs.map(j => [j.lat, j.lng]) : []),
    ...(layers.equipment ? equipment.map(e => [e.lat, e.lng]) : []),
    ...(layers.crew && canSeeCrew ? crewPlaced.map(c => [c.plat, c.plng]) : []),
    ...(layers.trucks ? trucks.map(t => [t.lat, t.lng]) : []),
  ];

  const counts = { jobs: jobs.length, equipment: equipment.length, crew: crew.length, trucks: trucks.length };
  const toggle = k => setLayers(l => ({ ...l, [k]: !l[k] }));
  const chip = (k, disabled) => {
    const m = LAYER_META[k]; const on = layers[k] && !disabled;
    return (
      <button key={k} onClick={() => !disabled && toggle(k)} disabled={disabled}
        style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '7px 12px', borderRadius: 20, border: `1.5px solid ${on ? m.color : 'var(--border)'}`, background: on ? m.color : 'var(--surface)', color: on ? '#fff' : (disabled ? '#9ca3af' : 'var(--text)'), fontSize: 13, fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.7 : 1 }}>
        <span style={{ width: 10, height: 10, borderRadius: '50%', background: on ? '#fff' : m.fill, border: on ? 'none' : `1px solid ${m.color}` }} />
        {m.label}{k in counts ? ` (${counts[k]})` : ''}
      </button>
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
        <button onClick={onBack} style={navBtn}>‹ Schedule</button>
        <div>
          <h1 style={{ fontSize: 21, fontWeight: 800, margin: 0 }}>Field Map</h1>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>Turn layers on and off. Trucks are live from Azuga; Who’s Working is each person’s clock-in spot, a snapshot rather than live tracking.</div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        {chip('jobs')}
        {chip('equipment')}
        {canSeeCrew && chip('crew')}
        {canSeeCrew && (trucksOn ? chip('trucks') : (
          <span title="Add AZUGA_USERNAME, AZUGA_PASSWORD and AZUGA_CLIENT_ID to the server to switch this on"
            style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '7px 12px', borderRadius: 20, border: '1.5px dashed var(--border)', background: 'var(--surface)', color: '#9ca3af', fontSize: 13, fontWeight: 700 }}>
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#e5e7eb' }} />
            🚚 Trucks · not connected
          </span>
        ))}
      </div>

      {err && <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', color: '#dc2626', fontSize: 14, marginBottom: 12 }}>{err}</div>}

      <div style={{ height: '66vh', minHeight: 380, borderRadius: 12, overflow: 'hidden', border: '1.5px solid var(--border)' }}>
        <MapContainer center={RENO} zoom={10} style={{ height: '100%', width: '100%' }} scrollWheelZoom>
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="© OpenStreetMap contributors" />
          <FitBounds points={points} />

          {layers.jobs && jobs.map(j => (
            <CircleMarker key={`j-${j.id}`} center={[j.lat, j.lng]} radius={9}
              pathOptions={{ color: LAYER_META.jobs.color, fillColor: LAYER_META.jobs.fill, fillOpacity: 0.85, weight: 2 }}>
              <Popup>
                <strong>{[j.project_number, j.name].filter(Boolean).join(' — ')}</strong><br />
                <span style={{ color: '#6b7280' }}>Job site</span>
              </Popup>
            </CircleMarker>
          ))}

          {layers.equipment && equipment.map(e => (
            <CircleMarker key={`e-${e.id}`} center={[e.lat, e.lng]} radius={7}
              pathOptions={{ color: LAYER_META.equipment.color, fillColor: LAYER_META.equipment.fill, fillOpacity: 0.85, weight: 2 }}>
              <Popup>
                <strong>{e.asset_number ? `${e.asset_number} · ` : ''}{[e.year, e.make, e.model].filter(Boolean).join(' ') || e.name}</strong><br />
                <span style={{ color: '#6b7280' }}>{e.location_label}</span>
              </Popup>
            </CircleMarker>
          ))}

          {layers.crew && canSeeCrew && crewPlaced.map((c, i) => (
            <CircleMarker key={`c-${i}`} center={[c.plat, c.plng]} radius={8}
              pathOptions={{ color: LAYER_META.crew.color, fillColor: LAYER_META.crew.fill, fillOpacity: 0.9, weight: 2 }}>
              <Popup>
                <strong>{c.name}</strong><br />
                <span style={{ color: '#374151' }}>{c.job}</span><br />
                <span style={{ color: '#6b7280', fontSize: 12 }}>
                  On the clock since {fmtTime(c.since)}<br />
                  {c.via === 'gps' ? `📍 Clock-in GPS${c.loc_at ? ` · ${new Date(c.loc_at).toLocaleDateString()}` : ''}` : '📍 At job location (no GPS)'}
                </span>
              </Popup>
            </CircleMarker>
          ))}

          {/* The trucks. Bigger than the other pins and ringed, because a moving
              vehicle is the one thing on this map you might act on right now. */}
          {layers.trucks && trucks.map(t => {
            const st = TRUCK_STATE[t.state] || TRUCK_STATE.unknown;
            return (
              <CircleMarker key={`t-${t.id}`} center={[t.lat, t.lng]} radius={10}
                pathOptions={{ color: LAYER_META.trucks.color, fillColor: st.dot, fillOpacity: 0.95, weight: 3 }}>
                <Popup>
                  <strong>🚚 {t.name}</strong><br />
                  {t.driver && <><span style={{ color: '#374151' }}>{t.driver}</span><br /></>}
                  <span style={{ color: '#6b7280', fontSize: 12 }}>
                    {st.word}{t.speed != null && t.state === 'moving' ? ` · ${t.speed} mph` : ''}<br />
                    {t.address && <>{t.address}<br /></>}
                    {t.at && <>Last fix {ago(t.at)}<br /></>}
                    {t.odometer != null && <>{t.odometer.toLocaleString()} mi on the clock</>}
                  </span>
                </Popup>
              </CircleMarker>
            );
          })}
        </MapContainer>
      </div>

      <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 8 }}>
        {canSeeCrew
          ? 'Who’s Working shows people currently on the clock, pinned at their clock-in GPS (or their job if GPS wasn’t shared). Trucks are live from Azuga. Refreshes every minute.'
          : 'Jobs & equipment locations. (The Who’s Working and Trucks layers are limited to project managers.)'}
      </div>
    </div>
  );
}

// ── Move Log ────────────────────────────────────────────────────────────────
// Every equipment move the shop has logged, newest first. The data was already
// there — the endpoint answers for the whole fleet — but the only screen reading
// it was a single job's Shop section, so a machine could be moved from the
// Equipment page and then have nowhere to show that it had moved.
//
// Read-only on purpose. This is for seeing what the shop is capturing; moves are
// created and completed where the work happens, on the job and on the equipment.
function MoveLogView({ onBack }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    apiFetch('/equipment-movements')
      .then(r => setRows(Array.isArray(r) ? r : []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  const fmt = iso => {
    if (!iso) return '—';
    const d = new Date(String(iso).length <= 10 ? `${iso}T12:00:00` : iso);
    return isNaN(d) ? '—' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const statuses = useMemo(() => [...new Set(rows.map(r => r.status).filter(Boolean))], [rows]);

  // One search box over everything a person would think to type: the machine, either
  // end of the move, who drove it, and the note.
  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return rows.filter(r => {
      if (statusFilter && r.status !== statusFilter) return false;
      if (!s) return true;
      return [r.equipment_name, r.equipment_category, r.from_label, r.to_label, r.driver, r.notes, r.status]
        .some(v => String(v || '').toLowerCase().includes(s));
    });
  }, [rows, q, statusFilter]);

  const th = { textAlign: 'left', padding: '9px 10px', fontSize: 11, fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap', borderBottom: '1px solid var(--border)' };
  const td = { padding: '9px 10px', fontSize: 13, color: 'var(--text)', borderBottom: '1px solid #f3f4f6', verticalAlign: 'top' };

  return (
    <div>
      <button onClick={onBack} style={{ background: 'none', border: 'none', color: '#e87722', fontSize: 13, fontWeight: 700, cursor: 'pointer', padding: 0, marginBottom: 10 }}>‹ Schedule</button>
      <h1 style={{ fontSize: 21, fontWeight: 800, margin: '0 0 4px' }}>Move Log</h1>
      <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginBottom: 14 }}>
        Every equipment move the shop has logged, newest first.
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
        <input value={q} onChange={e => setQ(e.target.value)}
          placeholder="Search machine, job, driver, note…"
          style={{ flex: 1, minWidth: 240, border: '1px solid var(--border)', borderRadius: 8, padding: '9px 12px', fontSize: 14 }} />
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '9px 10px', fontSize: 13, background: 'var(--surface)' }}>
          <option value="">All statuses</option>
          {statuses.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)', fontWeight: 700 }}>
          {shown.length === rows.length ? `${rows.length} moves` : `${shown.length} of ${rows.length}`}
        </span>
      </div>

      {loading ? (
        <div style={{ color: 'var(--text-muted)', fontSize: 14, padding: '24px 0' }}>Loading…</div>
      ) : shown.length === 0 ? (
        <div style={{ color: 'var(--text-muted)', fontSize: 14, padding: '24px 0' }}>
          {rows.length === 0 ? 'No moves logged yet.' : 'Nothing matches that search.'}
        </div>
      ) : (
        <div className="admin-card" style={{ padding: 0, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 860 }}>
            <thead>
              <tr>
                <th style={th}>Date</th>
                <th style={th}>Equipment</th>
                <th style={th}>From</th>
                <th style={th}>To</th>
                <th style={th}>Driver</th>
                <th style={th}>Status</th>
                <th style={th}>Notes</th>
              </tr>
            </thead>
            <tbody>
              {shown.map(m => (
                <tr key={m.id}>
                  <td style={{ ...td, whiteSpace: 'nowrap', color: 'var(--text-muted)' }}>{fmt(m.moved_at || m.created_at)}</td>
                  <td style={{ ...td, fontWeight: 700 }}>
                    {m.equipment_name || '—'}
                    {m.equipment_category && <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-muted)' }}>{m.equipment_category}</div>}
                  </td>
                  <td style={td}>{m.from_label || <span style={{ color: '#c4c4c4' }}>—</span>}</td>
                  <td style={{ ...td, fontWeight: 600 }}>{m.to_label || <span style={{ color: '#c4c4c4' }}>—</span>}</td>
                  <td style={td}>{m.driver || <span style={{ color: '#c4c4c4' }}>—</span>}</td>
                  <td style={td}>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 4, whiteSpace: 'nowrap',
                      background: m.status === 'completed' ? '#dcfce7' : '#fef3c7',
                      color: m.status === 'completed' ? '#15803d' : '#b45309' }}>
                      {m.status === 'completed' ? 'Completed' : (m.status || 'planned').replace(/\b\w/g, c => c.toUpperCase())}
                    </span>
                  </td>
                  <td style={{ ...td, color: 'var(--text-muted)', maxWidth: 280, whiteSpace: 'pre-wrap' }}>{m.notes || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Said plainly rather than left for someone to discover: the log records where
          and when, not the hour meter, so it cannot answer "how many hours were on it
          when we dropped it off". */}
      <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 12, lineHeight: 1.5 }}>
        The shop records the machine, both ends of the move, who drove it and when.
        Hour-meter readings are <strong>not</strong> captured on a move, so hours run on a job
        cannot be worked out from this log.
      </div>
    </div>
  );
}
